import {Component, Input} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {NODEMAILER_EMAIL_REGEX, nodemailerEmailValidator} from '../../../shared-utils/form/email.validator';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {getDeepCopyOfObject} from '../../../shared-utils/object/object.utils';
import {
  clearAllAutoOrderSupplierEmails,
  sendAutoOrderEmails,
  setAutoOrderSupplierEmails,
  updateAutoOrderOrderEmailRecipients,
  updateAutoOrderStatusField,
} from '../../../../features/auto-ordering/store/auto-ordering.actions';
import {Store} from '@ngrx/store';
import {ISharedModalBasic} from '../../../shared-models/modals/shared-modal-basic';
import {SharedModalBasicComponent} from '../shared-modal-basic/shared-modal-basic.component';
import {IStore} from '../../../shared-models/store/store';
import {IAutoOrderOrder} from '../../../../features/auto-ordering/models';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {ModalControllerService} from '../../../shared-services/overlay/modal-controller.service';
import {
  AutoOrderAllStatusTypesEnum,
  AutoOrderEmailStatusType,
} from '../../../../features/auto-ordering/models/auto-order-order-status-types.enum';

@Component({
  selector: 'app-shared-modal-order-recipients',
  templateUrl: './shared-modal-order-recipients.component.html',
  styleUrls: ['./shared-modal-order-recipients.component.scss'],
  standalone: false,
})
export class SharedModalOrderRecipientsComponent {

  modalTitle = `Email Recipients`;
  order: IAutoOrderOrder;
  recipients: string[] = [];
  supplierEmails: Record<string, {selected: string[]; additional: string[]}> = {};
  recipientForm: FormGroup;
  autoOrderAllStatusTypesEnum = AutoOrderAllStatusTypesEnum;
  @Input() modalController: ModalControllerService;
  iconColour: IconColourEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons: typeof IconsEnum = IconsEnum;
  @Input() readonly userStore!: IStore;

  constructor(
    private formBuilder: FormBuilder,
    private readonly store: Store,
  ) {
    this.recipientForm = this.formBuilder.group({
      newRecipients: this.formBuilder.array<FormControl<string>>([]),
    });
  }

  get getNewRecipientsArray(): FormArray {
    return this.recipientForm.get('newRecipients') as FormArray<FormControl<string>>;
  }

  get getNewRecipientsControlArray(): FormControl<string>[] {
    return (this.recipientForm.get('newRecipients') as FormArray<FormControl<string>>).controls;
  }

  get getButtonSendEmailsTooltip(): string {
    if (this.order.pdfStatus === AutoOrderAllStatusTypesEnum.FAILED) {
      return 'You can not send emails if PDF has failed';
    }
    return 'Resend Emails to All Recipients';
  }

  get orderErrorCode(): string {
    const error = this.order.errorMsg;

    return error && typeof error !== 'string' ? error.code : '';
  }

  get orderErrorMsgObject(): { code: string; message: string } | null {
    const error = this.order.errorMsg;

    return error && typeof error !== 'string' ? error : null;
  }

  get orderErrorMsgString(): string {
    const error = this.order.errorMsg;

    return typeof error === 'string' ? error : '';
  }

  get getIsFormValid(): boolean {
    return !this.recipientForm.valid;
  }

  @Input() set setOrder(order: IAutoOrderOrder) {
    if (order) {
      this.order = getDeepCopyOfObject(order);
      this.recipients = getDeepCopyOfObject(order.recipients);
      this.switchOrderEmailStatus(order.emailStatus);
    }
  }

  @Input() set setSupplierEmails(emails: Record<string, {selected: string[]; additional: string[]}>) {
    if (emails && Object.keys(emails).length > 0) {
      this.supplierEmails = emails;
    }
  }

  protected isEmailValid(email: string): boolean {
    return NODEMAILER_EMAIL_REGEX.test(email);
  }

  getCanRecipientBeDeleted(): boolean {
    return this.order.emailStatus !== 'SENT' && this.order.emailStatus !== 'PENDING';
  }

  onClose(): void {
    this.store.dispatch(clearAllAutoOrderSupplierEmails());
    void this.modalController.dismiss();
  }

  onSkipEmailsClick(): void {
    this.store.dispatch(updateAutoOrderStatusField({
      orderId: this.order.orderId,
      field: 'emailStatus',
      status: 'SKIP',
    }));
    this.order.emailStatus = 'SKIP';
    this.store.dispatch(updateAutoOrderStatusField({
      orderId: this.order.orderId,
      field: 'errorMsg',
      status: null,
    }));
    this.order.errorMsg = null;
    if (this.order.pdfStatus === 'SUCCESS' && this.order.iqStatus === 'SUCCESS') {
      this.store.dispatch(updateAutoOrderStatusField({
        orderId: this.order.orderId,
        field: 'status',
        status: 'SUCCESS',
      }));
      this.order.status = 'SUCCESS';
    }
    this.switchOrderEmailStatus('SKIP');
  }

  onRecipientAdd(): void {
    this.getNewRecipientsArray.push(new FormControl('', [Validators.required, nodemailerEmailValidator]));
  }

  onRecipientDelete(index: number): void {
    this.recipients.splice(index, 1);
    const uniqueRecipients: string[] = this.convertFormControlsToEmailArray();
    this.store.dispatch(updateAutoOrderOrderEmailRecipients({
      orderId: this.order.orderId,
      recipients: uniqueRecipients,
    }));
  }

  onRecipientDeleteFromNew(index: number): void {
    this.getNewRecipientsArray.removeAt(index);
  }

  onResendEmails(): void {
    this.switchOrderEmailStatus('PENDING');
    this.store.dispatch(sendAutoOrderEmails({order: this.order}));
    void this.modalController.dismiss().then();
    void this.showBusySendingEmail(this.order.recipients);
  }

  onSaveRecipientsClick(): void {
    const uniqueRecipients: string[] = this.convertFormControlsToEmailArray();
    this.store.dispatch(updateAutoOrderOrderEmailRecipients({
      orderId: this.order.orderId,
      recipients: uniqueRecipients,
    }));
    this.store.dispatch(setAutoOrderSupplierEmails({
      store: this.userStore,
      recipientEmails: uniqueRecipients,
      supplierId: this.order.supplierId,
      typeOfEmail: 'additional',
    }));
    this.store.dispatch(setAutoOrderSupplierEmails({
      store: this.userStore,
      recipientEmails: uniqueRecipients,
      supplierId: this.order.supplierId,
      typeOfEmail: 'selected',
    }));
    void this.modalController.dismiss().then();
  }

  onSaveAndSendNewEmails(): void {
    const uniqueRecipients: string[] = this.convertFormControlsToEmailArray();
    this.store.dispatch(updateAutoOrderOrderEmailRecipients({
      orderId: this.order.orderId,
      recipients: uniqueRecipients,
    }));
    this.store.dispatch(setAutoOrderSupplierEmails({
      store: this.userStore,
      recipientEmails: uniqueRecipients,
      supplierId: this.order.supplierId,
      typeOfEmail: 'additional',
    }));
    this.store.dispatch(setAutoOrderSupplierEmails({
      store: this.userStore,
      recipientEmails: uniqueRecipients,
      supplierId: this.order.supplierId,
      typeOfEmail: 'selected',
    }));
    this.switchOrderEmailStatus('PENDING');
    this.store.dispatch(sendAutoOrderEmails({order: this.order}));
    void this.modalController.dismiss().then();
    void this.showBusySendingEmail(uniqueRecipients);
  }


  private convertFormControlsToEmailArray(): string[] {
    const formControls = this.getNewRecipientsArray.controls;
    const newRecipientEmails: string[] = [];
    formControls.forEach((control: FormControl<string>) => {
      if (control.valid && control.value && control.value !== '') {
        newRecipientEmails.push(control.value);
      }
    });
    const allRecipients: string[] = [...this.recipients, ...newRecipientEmails];
    return [...new Set(allRecipients)];
  }

  private switchOrderEmailStatus(newStatus: AutoOrderEmailStatusType): void {
    if (this.order.emailStatus !== newStatus) {
      this.order.emailStatus = newStatus;
    }
    switch (this.order.emailStatus) {
      case 'SENT':
        this.iconColour = this.iconColourEnum.green;
        this.modalTitle = 'Order Email Recipients - Succeeded';
        break;
      case 'FAILED':
        this.iconColour = this.iconColourEnum.red;
        this.modalTitle = 'Order Email Recipients - Failed';
        break;
      case 'PENDING':
        this.iconColour = this.iconColourEnum.yellow;
        this.modalTitle = 'Order Email Recipients Busy Processing';
        break;
      case 'SKIP':
        this.iconColour = this.iconColourEnum.green;
        this.modalTitle = 'Order Email Recipients - Skipped Send';
        break;
      default:
        break;
    }
  }

  private async showBusySendingEmail(emails: string[]): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: false,
      buttonCloseCross: false,
      buttonCloseText: '',
      modalTitle: `Generating Email`,
      modalTitleIcon: this.icons.emailCheckOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: `Sending Emails`,
      contextDescription: [
        `The server is busy sending emails to the following emails:`,
        ...emails,
      ],
      cssClass: ['shared-basic-modal-css-medium'],
    };
    const busyModalController = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps: modalProps,
      cssClass: [...modalProps.cssClass ?? 'shared-basic-modal-css'],
    });
    await busyModalController.present();
    setTimeout(async (): Promise<void> => {
      await busyModalController.dismiss();
    }, 2000);
  }
}
